import React, { useEffect, useState } from "react";
import {
  SlidersHorizontal,
  RotateCcw,
  Save,
  Info,
  PiggyBank,
  ShieldCheck,
  Target,
  Layers,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, fmtINR, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const CLASS_LABELS = {
  equity: "Equity",
  hybrid: "Hybrid",
  debt: "Debt",
  gold: "Gold",
  real_estate: "Real Estate",
  cash: "Cash",
  crypto: "Crypto",
  other: "Other",
};
const CLASS_ORDER = [
  "equity",
  "hybrid",
  "debt",
  "gold",
  "real_estate",
  "cash",
  "crypto",
  "other",
];

const MACRO_FIELDS = [
  {
    key: "inflation",
    label: "Inflation",
    suffix: "%",
    hint: "Default rate goals inflate at",
  },
  {
    key: "post_retirement_return",
    label: "Post-retirement return",
    suffix: "%",
    hint: "Return during drawdown",
  },
  {
    key: "withdrawal_rate",
    label: "Safe withdrawal rate",
    suffix: "%",
    hint: "Rule-of-thumb / FI SWR",
  },
  {
    key: "life_expectancy",
    label: "Life expectancy",
    suffix: "yrs",
    hint: "Used for drawdown horizon",
  },
  {
    key: "default_retirement_age",
    label: "Default retirement age",
    suffix: "yrs",
    hint: "Used when not set on profile",
  },
];

const BAND_STYLES = {
  "On Track": "text-emerald-700 dark:text-emerald-300",
  "Slightly Behind": "text-amber-700 dark:text-amber-300",
  "At Risk": "text-rose-700 dark:text-rose-300",
};

export default function Assumptions() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const [isDefault, setIsDefault] = useState(true);
  const [macro, setMacro] = useState({});
  const [classes, setClasses] = useState({});
  const [annuityDue, setAnnuityDue] = useState(false);
  const [impact, setImpact] = useState(null);
  const [justUpdated, setJustUpdated] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const a = await api.get("/assumptions");
        const o = await api
          .get("/planning/overview")
          .then((r) => r.data)
          .catch(() => null);
        applyData(a.data);
        setImpact(o);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function applyData(d) {
    const data = d.assumptions || {};
    setVersion(d.version);
    setIsDefault(d.is_default);
    setMacro({
      inflation: data.inflation,
      post_retirement_return: data.post_retirement_return,
      withdrawal_rate: data.withdrawal_rate,
      life_expectancy: data.life_expectancy,
      default_retirement_age: data.default_retirement_age,
    });
    setAnnuityDue(!!data.annuity_due);
    setClasses(data.asset_classes || {});
  }

  const setClassField = (cls, field, value) => {
    setClasses((prev) => ({
      ...prev,
      [cls]: { ...prev[cls], [field]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        inflation: num(macro.inflation),
        post_retirement_return: num(macro.post_retirement_return),
        withdrawal_rate: num(macro.withdrawal_rate),
        life_expectancy: num(macro.life_expectancy),
        default_retirement_age: num(macro.default_retirement_age),
        annuity_due: annuityDue,
        asset_classes: Object.fromEntries(
          Object.entries(classes).map(([k, v]) => [
            k,
            {
              return: num(v.return),
              volatility: num(v.volatility),
              tax_rate: num(v.tax_rate),
            },
          ]),
        ),
        label: "Custom",
      };
      const { data } = await api.put("/assumptions", payload);
      applyData(data);
      const o = await api
        .get("/planning/overview")
        .then((r) => r.data)
        .catch(() => null);
      setImpact(o);
      setJustUpdated(true);
      setTimeout(() => setJustUpdated(false), 2500);
      toast.success(
        `Assumptions saved — projections updated (v${data.version})`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all assumptions to AccessWealth defaults?"))
      return;
    setSaving(true);
    try {
      const { data } = await api.post("/assumptions/reset");
      applyData(data);
      const o = await api
        .get("/planning/overview")
        .then((r) => r.data)
        .catch(() => null);
      setImpact(o);
      setJustUpdated(true);
      setTimeout(() => setJustUpdated(false), 2500);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">
            Planning engine
          </p>
          <h1
            data-testid="assumptions-title"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Assumptions
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Every goal, retirement and FI number is computed from these rates.
            Saved as a versioned set, so each projection stays reproducible.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-500 px-2.5 py-1 rounded-full font-semibold">
            <Info className="h-3 w-3" /> v{version} ·{" "}
            {isDefault ? "default" : "custom"}
          </span>
          <button
            data-testid="assumptions-reset-btn"
            onClick={reset}
            disabled={saving}
            className="btn-secondary !text-xs"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid lg:grid-cols-3 gap-6 animate-pulse">
          <div className="h-96 rounded-2xl bg-slate-100 dark:bg-slate-800 lg:col-span-2" />
          <div className="h-96 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* EDITOR */}
          <div className="lg:col-span-2 space-y-6">
            {/* Macro */}
            <section className="card-soft p-6" data-testid="macro-card">
              <div className="flex items-center gap-2 mb-1">
                <SlidersHorizontal className="h-4 w-4 text-emerald-600" />
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                  Macro assumptions
                </h2>
              </div>
              <p className="text-xs text-slate-500 mb-5">
                Inflation, drawdown and longevity inputs.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {MACRO_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      {f.label}
                    </label>
                    <div className="relative mt-1">
                      <input
                        data-testid={`macro-${f.key}`}
                        type="number"
                        step="0.1"
                        value={macro[f.key] ?? ""}
                        onChange={(e) =>
                          setMacro({ ...macro, [f.key]: e.target.value })
                        }
                        className="input-soft pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                        {f.suffix}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">{f.hint}</p>
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <Checkbox
                      data-testid="macro-annuity-due"
                      checked={annuityDue}
                      onCheckedChange={(checked) =>
                        setAnnuityDue(checked === true)
                      }
                    />

                    <span>
                      Contributions invested at the start of each month
                      (annuity-due)
                    </span>
                  </label>
                </div>
              </div>
            </section>

            {/* Asset class expectations */}
            <section className="card-soft p-6" data-testid="classes-card">
              <div className="flex items-center gap-2 mb-1">
                <Layers className="h-4 w-4 text-emerald-600" />
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                  Asset-class expectations
                </h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Expected annual return, volatility and approximate tax on gains.
                Used when an asset has no explicit return.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                      <th className="py-2 pr-3 font-semibold">Class</th>
                      <th className="py-2 px-2 font-semibold">Return %</th>
                      <th className="py-2 px-2 font-semibold">Volatility %</th>
                      <th className="py-2 px-2 font-semibold">Tax %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLASS_ORDER.filter((c) => classes[c]).map((cls) => (
                      <tr
                        key={cls}
                        className="border-t border-slate-100 dark:border-slate-800"
                      >
                        <td className="py-2 pr-3 font-medium text-slate-800 dark:text-slate-200 whitespace-nowrap">
                          {CLASS_LABELS[cls] || cls}
                        </td>
                        {["return", "volatility", "tax_rate"].map((field) => (
                          <td key={field} className="py-2 px-1">
                            <input
                              data-testid={`class-${cls}-${field}`}
                              type="number"
                              step="0.1"
                              value={classes[cls]?.[field] ?? ""}
                              onChange={(e) =>
                                setClassField(cls, field, e.target.value)
                              }
                              className="input-soft !py-1.5 w-20"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="flex justify-end">
              <button
                data-testid="assumptions-save-btn"
                onClick={save}
                disabled={saving}
                className="btn-primary"
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving…" : "Save & re-project"}
              </button>
            </div>
          </div>

          {/* IMPACT PREVIEW */}
          <div className="lg:col-span-1">
            <div
              data-testid="impact-card"
              className={`card-soft p-6 lg:sticky lg:top-6 transition-shadow ${
                justUpdated ? "ring-2 ring-emerald-400" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-500">
                  Live impact
                </div>
                {justUpdated && (
                  <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 animate-pulse">
                    Updated
                  </span>
                )}
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white mt-1">
                How your plan reacts
              </h3>
              <p className="text-[11px] text-slate-400 mt-1">
                Save to recompute every projection with these assumptions.
              </p>

              {!impact ? (
                <p className="mt-6 text-sm text-slate-500">
                  Add assets & goals to preview the impact.
                </p>
              ) : (
                <div className="mt-5 space-y-4">
                  <ImpactRow
                    icon={PiggyBank}
                    label="Retirement corpus required"
                    value={
                      impact.retirement?.required_corpus == null
                        ? "Add expenses"
                        : fmtINR(impact.retirement.required_corpus, {
                            compact: true,
                          })
                    }
                    sub={
                      impact.retirement?.readiness_band
                        ? impact.retirement.readiness_band
                        : null
                    }
                    subClass={BAND_STYLES[impact.retirement?.readiness_band]}
                    testid="impact-retirement-required"
                  />
                  <ImpactRow
                    icon={PiggyBank}
                    label="Retirement projected"
                    value={fmtINR(impact.retirement?.projected_corpus || 0, {
                      compact: true,
                    })}
                    testid="impact-retirement-projected"
                  />
                  <ImpactRow
                    icon={ShieldCheck}
                    label="FI number"
                    value={
                      impact.financial_independence?.fi_number == null
                        ? "Add expenses"
                        : fmtINR(impact.financial_independence.fi_number, {
                            compact: true,
                          })
                    }
                    sub={
                      impact.financial_independence?.fi_progress_pct != null
                        ? `${impact.financial_independence.fi_progress_pct}% funded`
                        : null
                    }
                    testid="impact-fi-number"
                  />
                  <ImpactRow
                    icon={Target}
                    label="Goals on track"
                    value={`${impact.goal_summary?.fully_funded || 0} / ${
                      impact.goal_summary?.total || 0
                    }`}
                    sub={`${impact.goal_summary?.at_risk || 0} at risk`}
                    subClass={
                      impact.goal_summary?.at_risk > 0
                        ? BAND_STYLES["At Risk"]
                        : BAND_STYLES["On Track"]
                    }
                    testid="impact-goals-ontrack"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function ImpactRow({ icon: Icon, label, value, sub, subClass, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center justify-between rounded-xl border border-slate-100 dark:border-slate-800 p-3"
    >
      <div className="flex items-center gap-2.5">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <div className="text-right">
        <div className="num-mono text-sm font-bold text-slate-900 dark:text-white">
          {value}
        </div>
        {sub && (
          <div
            className={`text-[10px] font-semibold ${subClass || "text-slate-400"}`}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function num(v) {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}
